import { createSlice, createSelector } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'

import { setTimeoutInDev } from '@/utils/environment'
import { createApiAsyncThunk } from '@/services/apiService'
import { sanitizeFormData, transformToFormData } from '@/utils/formData'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { updateCurrentBien } from '../biens/biensSlice'
import { FORM_TYPES } from './MandatFormContext'

export const mandatTypes = {
  location: "location",
  vente: "vente"
}

export const mandatFormRules = (getValues) => ({
  type_mandat: { required: "Le type de mandat est requis" },
  date_signature: { required: "La date de signature est requise" },
  date_fin: {
    validate: (value) => {
      if(!value) return
      const date1 = DateTime.fromISO(getValues('date_signature'))
      const date2 = DateTime.fromISO(value)
      return date2 > date1 || 'doit être postérieure à la date de signature'
    }
  }
})

const URL = 'mandats'
const getUrl = (bien_id, id) => {
  const url = `biens/${bien_id}/${URL}`
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10 
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
}

const sanitizeMandatFormData = (data) => {
  const formData = sanitizeFormData(data)
  delete formData.bien_id
  delete formData.bien
  delete formData.proprietaires_signataires
  delete formData.cle_dispo
  delete formData.remise_cles_file
  
  const fileFields = ["mandat_file"]

  // fileFields.forEach(
  //   fieldAttr => {
  //     if (formData[fieldAttr] === undefined || formData[fieldAttr] === '') {
  //       delete formData[fieldAttr]
  //     }
  //   }
  // )

  return transformToFormData(formData, "mandat", fileFields)
}

// Fonction utilitaire pour synchroniser les mandats du bien courant
const syncCurrentBienMandats = (currentBien, mandatResponse, operationType = 'update', mandatId = null) => {
  let updatedMandats = [...(currentBien.mandats || [])]
  
  if (operationType === 'create') {
    // Ajouter le nouveau mandat
    updatedMandats.push(mandatResponse)
  } else if (operationType === 'update') {
    // Si le mandat mis à jour a une date de fin : le retirer de la liste des mandats du bien
    if (mandatResponse.date_fin) {
      updatedMandats = updatedMandats.filter(m => m.id !== mandatId)
    } else {
      // Si le mandat mis à jour n'a pas de date de fin
      const existingMandatIndex = updatedMandats.findIndex(m => m.id === mandatId)
      if (existingMandatIndex !== -1) {
        // Le mandat existe déjà, le mettre à jour
        updatedMandats[existingMandatIndex] = mandatResponse
      } else {
        // Le mandat n'existe pas dans la liste, l'ajouter
        updatedMandats.push(mandatResponse)
      }
    }
  } else if (operationType === 'delete') {
    // Si le mandat mis à jour a une date de fin : le retirer de la liste des mandats du bien
    mandatId = mandatId || mandatResponse.id
    updatedMandats = updatedMandats.filter(m => m.id !== mandatId)
  }
  
  // Trier les mandats par numéro
  updatedMandats.sort((a, b) => {
    const numA = a.numero ? parseInt(a.numero, 10) : 0
    const numB = b.numero ? parseInt(b.numero, 10) : 0
    return numB - numA
  })
  
  return updatedMandats
}

// Thunks
export const fetchMandats = createApiAsyncThunk(
  'mandats/fetchList',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const bien_id = params.bien_id
      delete params.bien_id

      await setTimeoutInDev()
      const response = await api
        .url(getUrl(bien_id))
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error('Error fetching mandats:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des mandats')
    }
  }
)

export const fetchMandat = createApiAsyncThunk(
  'mandats/fetchItem',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const bien_id = params.bien_id
      const mandat_id = params.mandat_id
      delete params.bien_id
      delete params.mandat_id

      if(!bien_id || !mandat_id) {
        throw new Error("un bien et un mandat doivent être désignés")
      }

      await setTimeoutInDev()
      const response = await api
        .url(getUrl(bien_id, mandat_id))
        .query()
        .get()

      return response
    } catch (error) {
      console.error('Error fetching mandat:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération du mandat')
    }
  }
)

const getSuccessNotification = (formType) => {
  let message
  switch(formType) {
    case FORM_TYPES.EDIT:
      message = "Détails du mandat mis à jour avec succès"
      break
    case FORM_TYPES.NEW:
      message = "Mandat créé avec succès"
      break
    case FORM_TYPES.CLOSE:
      message = "Mandat terminé avec succès"
      break
  }
  return addFlashMessage({type: 'success', text: message})
}

export const createMandat = createApiAsyncThunk(
  'mandats/create',
  async (params, { api, dispatch, getState, rejectWithValue }) => {
    const { bien_id, formType, data} = params
    try {
      if(!bien_id) throw new Error("Un mandat doit être attaché à un bien")

      const response = await api
        .url(getUrl(bien_id))
        .post(sanitizeMandatFormData(data))

      dispatch(setCurrentMandat(response))

      const currentBien = getState().biens?.current
      const updatedMandats = syncCurrentBienMandats(currentBien, response, 'create')
      
      dispatch(updateCurrentBien({
        mandats: updatedMandats
      }))
      
      dispatch(getSuccessNotification(formType))

      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateMandat = createApiAsyncThunk(
  'mandats/update',
  async ({ bien_id, id, data, formType }, { api, dispatch, getState, rejectWithValue }) => {
    try {
      const sanitizedData = sanitizeMandatFormData(data)

      let requestBody

      // sanitizeMandatFormData retourne un FormData, pour gérer les fichiers
      requestBody = sanitizedData
      
      const response = await api
        .url(getUrl(bien_id, id))
        .put(requestBody)

      dispatch(setCurrentMandat(response))

      const currentBien = getState().biens?.current
      const updatedMandats = syncCurrentBienMandats(currentBien, response, 'update', id)
      
      dispatch(updateCurrentBien({
        mandats: updatedMandats
      }))

      dispatch(getSuccessNotification(formType))

      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteMandat = createApiAsyncThunk(
  'mandats/delete',
  async (mandat, { api, dispatch, getState, rejectWithValue }) => {
    try {
      api
        .url(getUrl(mandat.bien_id, mandat.id))
        .delete()

      const currentBien = getState().biens?.current
      const updatedMandats = syncCurrentBienMandats(currentBien, mandat, 'delete')
      
      dispatch(updateCurrentBien({
        mandats: updatedMandats
      }))
      dispatch(addFlashMessage({type: 'success', text: "Le mandat a été supprimé avec succès"}))
      return mandat
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const createMandatWithProprietaires = createApiAsyncThunk(
  'mandats/createWithProprietaires',
  async (params, { api, dispatch, getState, rejectWithValue }) => {
    const { bien_id, mandat, proprietaires, bien} = params

    try {
      // ÉTAPE 1 : Créer le mandat avec les propriétaires
      const sanitizedData = sanitizeMandatFormData(mandat)
      
      let requestBody = sanitizedData
      
      // Ajouter les propriétaires au FormData
      if (proprietaires && Array.isArray(proprietaires)) {
        proprietaires.forEach((proprietaire, index) => {
          Object.entries(proprietaire).forEach(([key, value]) => {
            requestBody.append(`proprietaires[${index}][${key}]`, value)
          })
        })
      }

      const mandatResponse = await api
        .url(`biens/${bien_id}/mandats`)
        .post(requestBody)

      // ÉTAPE 3 : Mettre à jour le state Redux
      const currentBien = getState().biens?.current
      const updatedMandats = syncCurrentBienMandats(currentBien, mandatResponse, 'create')
      
      const updatedBien = {
        mandats: updatedMandats
      }
      
      // Vérifier que response.bien existe et contient les propriétaires
      if (mandatResponse.bien && mandatResponse.bien.proprietaires) {
        updatedBien.proprietaires = mandatResponse.bien.proprietaires
      }
      
      dispatch(updateCurrentBien(updatedBien))
      
      dispatch(addFlashMessage({
        type: 'success',
        text: "Mandat créé et propriétaires mis à jour avec succès"
      }))

      return mandatResponse
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateMandatWithProprietaires = createApiAsyncThunk(
  'mandats/updateWithProprietaires',
  async (params, { api, getState, dispatch, rejectWithValue }) => {
    const { bien_id, id, mandat, proprietaires, bien } = params
    
    try {
      // ÉTAPE 1 : Mettre à jour le mandat avec les propriétaires
      const sanitizedData = sanitizeMandatFormData(mandat)
      
      let requestBody = sanitizedData
      
      // Ajouter les propriétaires au FormData
      if (proprietaires && Array.isArray(proprietaires)) {
        proprietaires.forEach((proprietaire, index) => {
          Object.entries(proprietaire).forEach(([key, value]) => {
            requestBody.append(`proprietaires[${index}][${key}]`, value)
          })
        })
      }

      const mandatResponse = await api
        .url(`biens/${bien_id}/mandats/${id}`)
        .put(requestBody)

      // ÉTAPE 3 : Mettre à jour le state Redux
      const currentBien = getState().biens?.current
      const updatedMandats = syncCurrentBienMandats(currentBien, mandatResponse, 'update', id)
      
      const updatedBien = {
        mandats: updatedMandats
      }
      
      // // Vérifier que response.bien existe et contient les propriétaires
      if (mandatResponse.bien && mandatResponse.bien.proprietaires) {
        updatedBien.proprietaires = mandatResponse.bien.proprietaires
      }
      
      dispatch(updateCurrentBien(updatedBien))
      
      dispatch(addFlashMessage({
        type: 'success',
        text: "Mandat et propriétaires mis à jour avec succès"
      }))

      return mandatResponse
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut 
export const emptyMandat = {
  bien_id: null,
  type_mandat: "",
  numero: "",
  date_signature: "",
  date_fin: "",
  exclusivite: false,
  proprietaires_signataires: []
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyMandat[key] || "") : value]
  )
)

// Slice
const mandatsSlice = createSlice({
  name: 'mandats',
  initialState,
  reducers: {
    setItems: (state,action) => {
      state.items = action.payload
    },
    setEmptyCurrentMandat: (state, action) => {
      const data = {
        ...emptyMandat,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentMandat: (state, action) => {
      const sanitizedData = {
        ...emptyMandat,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    setMandatInEdition: (state, action) => {
      state.inEdition = {
        ...emptyMandat,
        ...action.payload
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentMandat: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    resetMandatInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchMandats
    builder.addCase(fetchMandats.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchMandats.fulfilled, (state, action) => {
      state.items = action.payload.mandats
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchMandats.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createMandat
    builder.addCase(createMandat.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createMandat.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createMandat.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateMandat
    builder.addCase(updateMandat.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateMandat.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateMandat.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Mandat
    builder.addCase(deleteMandat.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteMandat.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteMandat.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })

    // createMandatWithProprietaires thunk
    builder.addCase(createMandatWithProprietaires.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createMandatWithProprietaires.fulfilled, (state, action) => {
      // Ajouter le nouveau mandat à la liste
      state.items.unshift(action.payload)
      
      // Mettre à jour le mandat courant
      state.current = action.payload
      
      // Réinitialiser le mandat en édition
      state.inEdition = null
      
      // Mettre à jour les statuts de chargement
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createMandatWithProprietaires.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload?.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateMandatWithProprietaires thunk
    builder.addCase(updateMandatWithProprietaires.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateMandatWithProprietaires.fulfilled, (state, action) => {
      const updatedMandat = action.payload
      
      // Mettre à jour le mandat dans la liste
      const index = state.items.findIndex(item => item.id === updatedMandat.id)
      if (index !== -1) {
        state.items[index] = updatedMandat
      }
      
      // Mettre à jour le mandat courant s'il correspond
      if (state.current?.id === updatedMandat.id) {
        state.current = updatedMandat
      }
      
      // Réinitialiser le mandat en édition
      state.inEdition = null
      
      // Mettre à jour les statuts de chargement
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateMandatWithProprietaires.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload?.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
  }
})

// Tools
export const isActiveMandat = (mandat) => 
  mandat.id && !!mandat.date_signature && !mandat.date_fin

export const setNewMandat = (mandat)  => {
  if(isActiveMandat(mandat)) {
    const { id, ...newMandat } = mandat
    newMandat.proprietaires_signataires = null
    newMandat.date_signature = null
    newMandat.date_fin = null
    newMandat.numero = ""
    newMandat.mandat_file = null
    return newMandat
  } else {
    return mandat
  }
}

// Sélecteurs
export const selectActiveMandats = createSelector(
  [state => state.mandats.items],
  (mandats) => mandats.filter(isActiveMandat)
)

export const selectMandatsByType = createSelector(
  [state => state.mandats.items, (state, type) => type],
  (mandats, type) => mandats.filter(mandat => mandat.type_mandat === type)
)

export const selectActiveMandatByType = createSelector(
  [selectActiveMandats, (state, type) => type],
  (activeMandats, type) => activeMandats.find(mandat => mandat.type_mandat === type)
)

export const selectActiveMandatsByBien = createSelector(
  [state => state.biens.current?.mandats],
  (mandats) => mandats?.filter(m => m.date_signature && !m.date_fin) || []
)

export const selectAvailableMandatTypes = createSelector(
  [selectActiveMandatsByBien],
  (activeMandats) => {
    const existingTypes = activeMandats.map(m => m.type_mandat)
    return ['location', 'vente'].filter(type => !existingTypes.includes(type))
  }
)

export const selectMandatRequestStatus = (state) => state.mandats.requestStatus

export const selectMandatCreationLoading = (state) => state.mandats.loadingStatus.create
export const selectMandatUpdateLoading = (state) => state.mandats.loadingStatus.update
export const selectMandatWithProprietairesLoading = createSelector(
  [selectMandatCreationLoading, selectMandatUpdateLoading],
  (creating, updating) => creating || updating
)

export const { 
  setEmptyCurrentMandat,
  setCurrentMandat,
  setItems,
  setMandatInEdition,
  resetCurrentMandat,
  resetMandatInEdition,
  setFilters,
  resetRequestStatus,
} = mandatsSlice.actions

export default mandatsSlice.reducer